import { APP_NAME } from "App";

import {
  CustomEventProperties,
  EventDataKey
} from "core/analytics/analytics.types";
import { getContextNameFromUrlBasePath } from "core/analytics/analytics.utils";
import { useAppInsightsTrackEvent } from "core/analytics/app-insights/app-insights.hooks";
import { initAppInsights } from "core/analytics/app-insights/app-insights.utils";
import { useInitGoogleAnalytics } from "core/analytics/google-analytics/google-analitics.hooks";
import { trackGaEvent } from "core/analytics/google-analytics/google-analitics.utils";
import { initHotJar } from "core/analytics/hotjar/hot-jar.utils";
import { useCurrentUserQuery } from "core/gateways/user/UserHooks";

import { useAccount } from "@azure/msal-react";

export const useAccountClaimAnalyticsParams = (): Partial<
  Record<EventDataKey | "userId", string | number | boolean | undefined>
> => {
  const account = useAccount();
  const claims = account?.idTokenClaims;

  return {
    userId: claims?.oid as string,
    tenantId: claims?.bpt as string
  };
};

const useCurrentUserAnalyticsParams = (): CustomEventProperties => {
  const { data: user } = useCurrentUserQuery();
  return { userRole: user?.roles.map(x => x.roleCode).join(",") };
};

const useDefaultAnalyticsParams = () => {
  const claimParams = useAccountClaimAnalyticsParams();
  const userParams = useCurrentUserAnalyticsParams();
  const appContext = getContextNameFromUrlBasePath();

  return { ...claimParams, ...userParams, appId: APP_NAME, appContext };
};

export const useInitAnalytics = () => {
  useInitGoogleAnalytics();
  initHotJar();
  const appInsightPlugin = initAppInsights();
  return { appInsightPlugin };
};

export const useTrackAnalyticsEvent = () => {
  const defaultParams = useDefaultAnalyticsParams();
  const trackAppInsightsEvent = useAppInsightsTrackEvent();

  return (name: string, params: CustomEventProperties) => {
    trackGaEvent(name, { ...defaultParams, ...params });
    trackAppInsightsEvent(name, { ...defaultParams, ...params });
  };
};
